import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Select from '../../components/ui/Select';
import realApiService from '../../utils/realApiService';
import useToast from '../../hooks/useToast';

const OrganizationSettings = () => {
  const { userProfile, currentOrganization, loading: profileLoading } = useUserProfile();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Check if user has access to organization settings
  const userRole = userProfile?.role?.toLowerCase();
  const hasAccess = userRole === 'owner' || userRole === 'admin';

  // Form state
  const [formData, setFormData] = useState({
    // Organization basic info
    name: '',
    description: '',
    
    // Member invitation settings
    allowAdminInvite: true,
    allowMemberInvite: false,
    requireDomainMatch: false,
    allowedDomains: '',
    
    // Notification preferences
    emailNotifications: true,
    taskNotifications: true,
    meetingNotifications: true,
    
    // Organization visibility
    visibility: 'private', // private, internal, public
    allowGuestAccess: false
  });

  // Load organization settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentOrganization?.id || !hasAccess) return;

      setIsLoading(true);
      setError(null);

      try {
        // Load organization basic info
        const orgResponse = await realApiService.organizations.getById(currentOrganization.id);
        
        // Load organization settings if available
        let settingsResponse = null;
        try {
          settingsResponse = await realApiService.get(`/api/v1/organizations/${currentOrganization.id}/settings`);
        } catch (settingsError) {
          // Settings might not exist yet, use defaults
          console.log('No existing settings found, using defaults');
        }

        setFormData({
          name: orgResponse.name || '',
          description: orgResponse.description || '',
          allowAdminInvite: settingsResponse?.allow_admin_create_projects ?? true,
          allowMemberInvite: settingsResponse?.allow_member_create_projects ?? false,
          requireDomainMatch: settingsResponse?.require_domain_match ?? false,
          allowedDomains: settingsResponse?.allowed_invitation_domains?.join(', ') || '',
          emailNotifications: settingsResponse?.enable_task_notifications ?? true,
          taskNotifications: settingsResponse?.enable_task_notifications ?? true,
          meetingNotifications: settingsResponse?.enable_meeting_notifications ?? true,
          visibility: 'private', // Default to private
          allowGuestAccess: false
        });

      } catch (error) {
        console.error('Failed to load organization settings:', error);
        setError('Failed to load organization settings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [currentOrganization?.id, hasAccess]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear messages when user starts editing
    if (error) setError(null);
    if (successMessage) setSuccessMessage('');
  };

  // Handle form submission
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!currentOrganization?.id) {
      setError('No organization selected');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      // Update organization basic info
      await realApiService.organizations.update(currentOrganization.id, {
        name: formData.name,
        description: formData.description
      });

      // Update organization settings
      const settingsData = {
        allow_admin_create_projects: formData.allowAdminInvite,
        allow_member_create_projects: formData.allowMemberInvite,
        require_domain_match: formData.requireDomainMatch,
        allowed_invitation_domains: formData.allowedDomains 
          ? formData.allowedDomains.split(',').map(d => d.trim()).filter(d => d)
          : null,
        enable_task_notifications: formData.taskNotifications,
        enable_meeting_notifications: formData.meetingNotifications,
        enable_role_change_notifications: true
      };

      try {
        await realApiService.put(`/api/v1/organizations/${currentOrganization.id}/settings`, settingsData);
      } catch (settingsError) {
        // If settings don't exist, create them
        if (settingsError.status === 404) {
          await realApiService.post(`/api/v1/organizations/${currentOrganization.id}/settings`, settingsData);
        } else {
          throw settingsError;
        }
      }

      setSuccessMessage('Organization settings saved successfully!');
      showToast('Organization settings saved successfully!', 'success');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Failed to save organization settings:', error);
      setError('Failed to save settings. Please try again.');
      showToast('Failed to save organization settings.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle organization deletion
  const handleDeleteOrganization = async () => {
    if (!currentOrganization?.id || deleteConfirmText !== currentOrganization?.name) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await realApiService.delete(`/api/v1/organizations/${currentOrganization.id}`);
      showToast('Organization deleted successfully', 'success');
      setShowDeleteModal(false);
      
      // Redirect to home after deletion
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete organization:', error);
      setError('Failed to delete organization. Please try again.');
      showToast('Failed to delete organization', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Show access denied if user doesn't have permission
  if (!profileLoading && !hasAccess) {
    return (
      <div className='p-8 text-center'>
        <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
          <Icon name='Lock' size={32} className='text-red-600' />
        </div>
        <div className='text-lg font-semibold mb-2 text-red-600'>Access Denied</div>
        <div className='text-sm text-gray-600 mb-4'>
          You need owner or admin permissions to access organization settings.
        </div>
        <div className='text-xs text-gray-500'>
          Current role: {userRole || 'No role assigned'}
        </div>
        <Button
          variant='outline'
          onClick={() => window.history.back()}
          className='mt-4'
          iconName='ArrowLeft'
        >
          Go Back
        </Button>
      </div>
    );
  }

  // Show loading state
  if (isLoading || profileLoading) {
    return (
      <div className='p-8 text-center'>
        <div className='flex flex-col items-center justify-center space-y-4'>
          <Icon name='Loader2' size={32} className='animate-spin text-primary' />
          <div className='text-lg text-muted-foreground'>
            Loading organization settings...
          </div>
        </div>
      </div>
    );
  }

  const visibilityOptions = [
    { value: 'private', label: 'Private - Only members can see this organization' },
    { value: 'internal', label: 'Internal - Anyone in your domain can see this organization' },
    { value: 'public', label: 'Public - Anyone can see this organization' }
  ];

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader
        userRole={userRole}
        currentUser={
          userProfile
            ? {
                name: userProfile.displayName || `${userProfile.firstName} ${userProfile.lastName}`,
                email: userProfile.email,
                role: userProfile.role,
              }
            : {
                name: 'Loading...',
                email: '',
                role: userRole,
              }
        }
        currentOrganization={currentOrganization}
      />

      <div className='max-w-4xl mx-auto py-8 px-6 mt-16 pb-24'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-foreground mb-2'>
            Organization Settings
          </h1>
          <p className='text-muted-foreground'>
            Manage your organization's basic information, member permissions, and preferences.
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
            <div className='flex items-center'>
              <Icon name='AlertCircle' size={20} className='text-red-600 mr-2' />
              <span className='text-red-800'>{error}</span>
            </div>
          </div>
        )}

        {successMessage && (
          <div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-lg'>
            <div className='flex items-center'>
              <Icon name='CheckCircle' size={20} className='text-green-600 mr-2' />
              <span className='text-green-800'>{successMessage}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSave} className='space-y-8'>
          {/* Organization Information */}
          <div className='bg-card rounded-lg border p-6'>
            <h2 className='text-xl font-semibold mb-4 flex items-center'>
              <Icon name='Building' size={20} className='mr-2' />
              Organization Information
            </h2>
            
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium mb-2'>Organization Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder='Enter organization name'
                  required
                />
              </div>
              
              <div>
                <label className='block text-sm font-medium mb-2'>Description</label>
                <textarea
                  className='w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder='Describe your organization...'
                />
              </div>
            </div>
          </div>

          {/* Member Invitation Settings */}
          <div className='bg-card rounded-lg border p-6'>
            <h2 className='text-xl font-semibold mb-4 flex items-center'>
              <Icon name='Settings2' size={20} className='mr-2' />
              Project Creation & Member Permissions
            </h2>
            
            <div className='space-y-4'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='allowAdminInvite'
                  checked={formData.allowAdminInvite}
                  onCheckedChange={(checked) => handleInputChange('allowAdminInvite', checked)}
                />
                <label htmlFor='allowAdminInvite' className='text-sm font-medium'>
                  Allow Admins to Create Projects
                </label>
              </div>
              
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='allowMemberInvite'
                  checked={formData.allowMemberInvite}
                  onCheckedChange={(checked) => handleInputChange('allowMemberInvite', checked)}
                />
                <label htmlFor='allowMemberInvite' className='text-sm font-medium'>
                  Allow Members to Create Projects
                </label>
              </div>
              
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='requireDomainMatch'
                  checked={formData.requireDomainMatch}
                  onCheckedChange={(checked) => handleInputChange('requireDomainMatch', checked)}
                />
                <label htmlFor='requireDomainMatch' className='text-sm font-medium'>
                  Require email domain match for invitations
                </label>
              </div>
              
              {formData.requireDomainMatch && (
                <div>
                  <label className='block text-sm font-medium mb-2'>Allowed Email Domains</label>
                  <Input
                    value={formData.allowedDomains}
                    onChange={(e) => handleInputChange('allowedDomains', e.target.value)}
                    placeholder='example.com, company.org'
                    helperText='Comma-separated list of allowed email domains'
                  />
                </div>
              )}
            </div>
          </div>

          {/* Notification Preferences */}
          <div className='bg-card rounded-lg border p-6'>
            <h2 className='text-xl font-semibold mb-4 flex items-center'>
              <Icon name='Bell' size={20} className='mr-2' />
              Notification Preferences
            </h2>
            
            <div className='space-y-4'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='taskNotifications'
                  checked={formData.taskNotifications}
                  onCheckedChange={(checked) => handleInputChange('taskNotifications', checked)}
                />
                <label htmlFor='taskNotifications' className='text-sm font-medium'>
                  Task assignments and updates
                </label>
              </div>
              
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='meetingNotifications'
                  checked={formData.meetingNotifications}
                  onCheckedChange={(checked) => handleInputChange('meetingNotifications', checked)}
                />
                <label htmlFor='meetingNotifications' className='text-sm font-medium'>
                  Meeting reminders and updates
                </label>
              </div>
            </div>
          </div>

          {/* Organization Visibility */}
          <div className='bg-card rounded-lg border p-6'>
            <h2 className='text-xl font-semibold mb-4 flex items-center'>
              <Icon name='Eye' size={20} className='mr-2' />
              Organization Visibility
            </h2>
            
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium mb-2'>Visibility Level</label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value) => handleInputChange('visibility', value)}
                  options={visibilityOptions}
                />
              </div>
              
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='allowGuestAccess'
                  checked={formData.allowGuestAccess}
                  onCheckedChange={(checked) => handleInputChange('allowGuestAccess', checked)}
                />
                <label htmlFor='allowGuestAccess' className='text-sm font-medium'>
                  Allow guest access to public projects
                </label>
              </div>
            </div>
          </div>

          {/* Danger Zone - Delete Organization (Owner Only) */}
          {userRole === 'owner' && (
            <div className='bg-card rounded-lg border border-red-200 dark:border-red-900/50 p-6'>
              <h2 className='text-xl font-semibold mb-4 flex items-center text-red-600 dark:text-red-400'>
                <Icon name='AlertTriangle' size={20} className='mr-2' />
                Danger Zone
              </h2>
              
              <div className='space-y-4'>
                <div className='p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800'>
                  <h3 className='font-semibold text-red-800 dark:text-red-300 mb-2'>Delete Organization</h3>
                  <p className='text-sm text-red-700 dark:text-red-400 mb-4'>
                    Once you delete an organization, there is no going back. This will permanently delete all projects, tasks, members, and data associated with this organization.
                  </p>
                  <Button
                    type='button'
                    variant='destructive'
                    onClick={() => setShowDeleteModal(true)}
                    iconName='Trash2'
                    iconPosition='left'
                    className='bg-red-600 hover:bg-red-700 text-white'
                  >
                    Delete Organization
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Fixed Action Bar */}
          <div className='fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-4 flex justify-center z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]'>
            <div className='max-w-4xl w-full flex justify-end px-6 items-center space-x-4'>
              {successMessage && (
                <span className='text-sm text-green-600 font-medium animate-in fade-in slide-in-from-right-2 duration-300'>
                  {successMessage}
                </span>
              )}
              <Button
                type='submit'
                variant='default'
                size='lg'
                loading={isSaving}
                iconName='Save'
                iconPosition='left'
                disabled={!hasAccess}
                className='px-8 shadow-md hover:shadow-lg transition-all duration-200'
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4'>
          <div className='bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200'>
            <div className='flex items-center gap-3 mb-4'>
              <div className='w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center'>
                <Icon name='AlertTriangle' size={24} className='text-red-600 dark:text-red-400' />
              </div>
              <div>
                <h3 className='text-lg font-bold text-slate-900 dark:text-slate-100'>Delete Organization</h3>
                <p className='text-sm text-slate-500 dark:text-slate-400'>This action cannot be undone</p>
              </div>
            </div>
            
            <div className='mb-6'>
              <p className='text-sm text-slate-600 dark:text-slate-300 mb-4'>
                This will permanently delete <strong className='text-red-600 dark:text-red-400'>{currentOrganization?.name}</strong> and all of its data, including:
              </p>
              <ul className='text-sm text-slate-600 dark:text-slate-400 space-y-1 ml-4'>
                <li className='flex items-center gap-2'>
                  <Icon name='Folder' size={14} className='text-slate-400' />
                  All projects and boards
                </li>
                <li className='flex items-center gap-2'>
                  <Icon name='CheckSquare' size={14} className='text-slate-400' />
                  All tasks and cards
                </li>
                <li className='flex items-center gap-2'>
                  <Icon name='Users' size={14} className='text-slate-400' />
                  All member associations
                </li>
                <li className='flex items-center gap-2'>
                  <Icon name='Calendar' size={14} className='text-slate-400' />
                  All meetings and schedules
                </li>
              </ul>
            </div>

            <div className='mb-6'>
              <label className='block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2'>
                Type <strong className='text-red-600'>{currentOrganization?.name}</strong> to confirm
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder='Enter organization name'
                className='border-red-200 focus:border-red-500 focus:ring-red-500'
              />
            </div>

            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className='flex-1'
              >
                Cancel
              </Button>
              <Button
                type='button'
                variant='destructive'
                onClick={handleDeleteOrganization}
                loading={isDeleting}
                disabled={deleteConfirmText !== currentOrganization?.name}
                className='flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
                iconName='Trash2'
                iconPosition='left'
              >
                {isDeleting ? 'Deleting...' : 'Delete Forever'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSettings;
